import { ulid } from 'ulid';
import { ApiClient } from './api/client';
import type { TokenSource } from './api/client';
import { Mirror } from './mirror/mirror';
import { Reports } from './mirror/reports';
import { TodayTakings } from './mirror/today-takings';
import { Sync } from './mirror/sync';
import type { ConnectionState } from './mirror/sync';
import { Outbox } from './outbox/outbox';
import type { OutboxSettlement } from './outbox/outbox';
import { settlementKind } from './outbox/intents';
import type { AppDb } from './db/schema';
import { clearDevice } from './db/schema';
import type { AuthProvider, SupportSessionState } from './auth/provider';
import {
  Appointment,
  Blackout,
  Certificate,
  ClientDetail,
  CreditAdded,
  HoursWritten,
  Preference,
  Product,
  Resource,
  Service,
  Settlement,
} from './wire';
import type { Appointment as AppointmentType, Session } from './wire';
import { isoDayIn } from './mirror/time';

/**
 * The composition root, and the app's one observable.
 *
 * React is not involved here on purpose. Everything §8 promises — the mirror,
 * the outbox, ordering, replay, conflicts becoming tasks — lives in plain
 * classes that a test can drive with a fake `fetch` and `fake-indexeddb` and no
 * renderer at all. The views subscribe to `revision` and re-read; that is the
 * entire binding, and it is why the crown-jewel tests are about semantics rather
 * than about pixels.
 */

export interface Runtime {
  db: AppDb;
  api: ApiClient;
  /**
   * `reporting-api` — a **second service**, so a second client.
   *
   * It carries the same credential (the staff ID token; all three roles may
   * read it, `readonly` deliberately included) and a different base URL,
   * because it runs against the read replica with its own three-connection pool
   * and must never share `core-api`'s. See `config.ts` for the routing
   * consequence of `''`.
   */
  reportingApi: ApiClient;
  mirror: Mirror;
  /** The last answer the ledger gave, and how to ask again. See `reports.ts`. */
  reports: Reports;
  /** Today's counter money by tender, the same way. See `today-takings.ts`. */
  todayTakings: TodayTakings;
  outbox: Outbox;
  sync: Sync;
  auth: AuthProvider;
  /**
   * The app's clock, in one place.
   *
   * A view that called `Date.now()` directly would be a view whose "today" the
   * mirror's day index could not be made to agree with — and it would be a view
   * no test could pin to a date. One clock, injected, used by the mirror, the
   * outbox and the calendar alike.
   */
  now(): number;
  /** Bumped on every change to the mirror, the outbox or the connection. */
  revision(): number;
  subscribe(listener: () => void): () => void;
  /**
   * Server events, as **cues** for the online screens.
   *
   * `subscribe` above is "something on this device changed" — the mirror, the
   * outbox, the connection — and it is what every offline-backed view listens
   * to. This is the other thing: "the server says something happened", typed,
   * with its subject. The kitchen screen wants `order.*`; the dock wants
   * `booking.*` on the day it is showing. Neither of them keeps a row, so
   * neither of them can be served by the mirror's revision, and polling alone
   * would make a pass wait fifteen seconds for a ticket that already exists.
   */
  subscribeEvents(listener: (type: string, subjectId: string | null) => void): () => void;
  connection(): ConnectionState;
  /**
   * **One online read, shared by every screen that asks for it by name.**
   *
   * `useRemote` is one subscription per hook call: two screens polling the
   * same route are two timers and two requests — which is what the desk and
   * the task list did for `GET /v1/stays/sync` when both were open (plan,
   * seventh pass L11). A read that answers the same question for the whole
   * app is kept here instead, keyed by name: the first subscriber starts the
   * one timer, later ones read the answer it already has, the last one to
   * leave stops the clock, and a re-open paints the last answer with its own
   * `fetchedAt` — the honest freshness line — until the fresh one lands.
   *
   * The loader is whatever the latest caller handed in; every caller of one
   * key passes the same request, so "latest wins" is a convenience rather
   * than a choice. `useSharedRemote` (`ui/context.tsx`) is the React binding
   * and the only intended caller.
   */
  shared<T>(key: string, load: (signal: AbortSignal) => Promise<T>, pollMs: number): SharedRead<T>;
  /**
   * **Is this device the console's read-only look, and until when?** `null`
   * for every real seat. Read off the credential rather than off the mirrored
   * session so the answer exists before the first hydrate — which is when the
   * outbox guard in `actions.ts` needs it and when the strip first paints.
   */
  supportSession(): SupportSessionState | null;
  signOut(): Promise<void>;
}

/** What a shared read holds at any moment — `Remote<T>` in `useRemote.ts`, without `reload`. */
export interface SharedSnapshot<T> {
  data: T | null;
  error: Error | null;
  /** True only while the first answer is still in flight. */
  loading: boolean;
  /** `runtime.now()` at the moment the answer landed. */
  fetchedAt: number | null;
}

export interface SharedRead<T> {
  /** The current snapshot — the same object until something changes, for `useSyncExternalStore`. */
  snapshot(): SharedSnapshot<T>;
  /** Listen; the first listener starts the poll and the last one leaving stops it. */
  subscribe(listener: () => void): () => void;
  /** Ask again now, on top of the poll. */
  reload(): void;
}

export interface RuntimeOptions {
  db: AppDb;
  auth: AuthProvider;
  baseUrl: string;
  /** Defaults to `baseUrl` — one origin, which is what a routed edge gives. */
  reportingBaseUrl?: string;
  fetchImpl?: typeof fetch;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  pastDays?: number;
  futureDays?: number;
  chunkDays?: number;
}

export function createRuntime(options: RuntimeOptions): Runtime {
  let revision = 0;
  const listeners = new Set<() => void>();
  const bump = (): void => {
    revision += 1;
    for (const listener of listeners) listener();
  };

  const eventListeners = new Set<(type: string, subjectId: string | null) => void>();
  const sharedReads = new Map<string, SharedEntry<unknown>>();

  const getToken: TokenSource = (force) => options.auth.token(force);
  const api = new ApiClient({
    baseUrl: options.baseUrl,
    getToken,
    ...(options.fetchImpl === undefined ? {} : { fetchImpl: options.fetchImpl }),
  });
  const reportingApi = new ApiClient({
    baseUrl: options.reportingBaseUrl ?? options.baseUrl,
    getToken,
    ...(options.fetchImpl === undefined ? {} : { fetchImpl: options.fetchImpl }),
  });

  const mirror = new Mirror({
    db: options.db,
    api,
    onChange: bump,
    ...(options.now === undefined ? {} : { now: options.now }),
    ...(options.pastDays === undefined ? {} : { pastDays: options.pastDays }),
    ...(options.futureDays === undefined ? {} : { futureDays: options.futureDays }),
    ...(options.chunkDays === undefined ? {} : { chunkDays: options.chunkDays }),
  });

  const reports = new Reports({
    db: options.db,
    api,
    onChange: bump,
    ...(options.now === undefined ? {} : { now: options.now }),
  });

  const todayTakings = new TodayTakings({
    db: options.db,
    api,
    onChange: bump,
    ...(options.now === undefined ? {} : { now: options.now }),
  });

  const outbox = new Outbox({
    db: options.db,
    api,
    onChange: bump,
    ...(options.now === undefined ? {} : { now: options.now }),
    onSettled: (settlement) => applySettlement(mirror, settlement),
  });

  let connection: ConnectionState = 'offline';
  const sync = new Sync({
    api,
    mirror,
    outbox,
    onState: (state) => {
      connection = state;
      bump();
    },
    onEvent: (type, subjectId) => {
      // Listener failures are that listener's problem: one screen throwing must
      // not end the stream, and the stream is the only thing every other screen
      // has.
      for (const listener of eventListeners) {
        try {
          listener(type, subjectId);
        } catch {
          /* ignored on purpose — see above */
        }
      }
    },
    ...(options.sleep === undefined ? {} : { sleep: options.sleep }),
  });

  return {
    db: options.db,
    api,
    reportingApi,
    mirror,
    reports,
    todayTakings,
    outbox,
    sync,
    auth: options.auth,
    now: options.now ?? Date.now,
    revision: () => revision,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    subscribeEvents(listener) {
      eventListeners.add(listener);
      return () => {
        eventListeners.delete(listener);
      };
    },
    connection: () => connection,
    shared<T>(key: string, load: (signal: AbortSignal) => Promise<T>, pollMs: number) {
      let entry = sharedReads.get(key) as SharedEntry<T> | undefined;
      if (entry === undefined) {
        entry = createSharedEntry<T>(options.now ?? Date.now);
        sharedReads.set(key, entry as SharedEntry<unknown>);
      }
      entry.load = load;
      entry.pollMs = pollMs;
      return entry;
    },
    supportSession: () => options.auth.support?.() ?? null,
    async signOut() {
      sync.stop();
      await options.auth.signOut();
      await clearDevice(options.db);
      bump();
    },
  };
}

/* ------------------------------------------------------------------ */
/* Shared online reads                                                 */
/* ------------------------------------------------------------------ */

interface SharedEntry<T> extends SharedRead<T> {
  load: (signal: AbortSignal) => Promise<T>;
  pollMs: number;
}

/**
 * One read, one timer, however many screens.
 *
 * The shape is `useRemote`'s, lifted out of React: the previous answer stays
 * beside a failure (a pass that blanked itself because one poll missed would
 * be less useful than one that says "this is from 4:12 and I could not reach
 * the server since"), a newer request aborts the older one, and nothing is
 * left running once the last listener has gone.
 */
function createSharedEntry<T>(now: () => number): SharedEntry<T> {
  const listeners = new Set<() => void>();
  let current: SharedSnapshot<T> = { data: null, error: null, loading: false, fetchedAt: null };
  let controller: AbortController | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;

  const publish = (next: Partial<SharedSnapshot<T>>): void => {
    current = { ...current, ...next };
    for (const listener of listeners) listener();
  };

  const entry: SharedEntry<T> = {
    load: () => Promise.reject(new Error('shared read has no loader yet')),
    pollMs: 0,
    snapshot: () => current,
    reload: () => {
      controller?.abort();
      const own = new AbortController();
      controller = own;
      if (current.fetchedAt === null && !current.loading) publish({ loading: true });
      void entry
        .load(own.signal)
        .then((data) => {
          if (own.signal.aborted) return;
          publish({ data, error: null, loading: false, fetchedAt: now() });
        })
        .catch((e: unknown) => {
          if (own.signal.aborted) return;
          publish({ error: e instanceof Error ? e : new Error(String(e)), loading: false });
        });
    },
    subscribe(listener) {
      listeners.add(listener);
      if (listeners.size === 1) {
        entry.reload();
        if (entry.pollMs > 0) timer = setInterval(() => entry.reload(), entry.pollMs);
      }
      return () => {
        listeners.delete(listener);
        if (listeners.size > 0) return;
        controller?.abort();
        controller = null;
        if (timer !== null) clearInterval(timer);
        timer = null;
        // A read nobody is watching is not "loading"; the last answer stays.
        if (current.loading) current = { ...current, loading: false };
      };
    },
  };
  return entry;
}

/**
 * What a settled intent does to the mirror — including the reconciliation the
 * optimistic row was waiting for.
 *
 * The server's answer carries `client_intent_id` (echoed by `withIntentId` in
 * `core-api`'s `intents.ts`) and the real row. Writing that row *is* the
 * reconciliation: the optimistic copy is not stored in the mirror at all, it is
 * derived from the outbox entry, and the entry has just moved to `done`. So the
 * pending line disappears and the real one appears in the same repaint, without
 * a reconciliation pass that could get the pairing wrong.
 *
 * The `client_intent_id` echo is still checked, because if it ever came back for
 * a *different* intent we would be writing one action's result under another's
 * name — and finding that out from a failed assertion is much cheaper than
 * finding it out from a support call.
 */
async function applySettlement(mirror: Mirror, settlement: OutboxSettlement): Promise<void> {
  const { entry, body } = settlement;
  const session = await mirror.session();
  const timezone = session?.tenant.timezone ?? 'UTC';

  const echoed = (body as { client_intent_id?: unknown } | null)?.client_intent_id;
  if (typeof echoed === 'string' && echoed !== entry.id) {
    throw new Error(
      `core-api echoed client_intent_id ${echoed} for intent ${entry.id}. ` +
        'A response matched to the wrong intent would reconcile the wrong row.',
    );
  }

  switch (settlementKind(entry.kind)) {
    case 'appointment': {
      const parsed = Appointment.safeParse(body);
      if (parsed.success) await mirror.putAppointment(parsed.data, timezone);
      return;
    }
    case 'client': {
      const parsed = ClientDetail.safeParse(body);
      if (parsed.success) await mirror.putClient(parsed.data);
      return;
    }
    case 'preference': {
      // A preference lives inside its client's detail row, so the settled
      // response is not the whole truth about the client — refetch when we can,
      // and let the next hydrate fix it when we cannot.
      const clientId = entry.target['client_id'];
      if (clientId === undefined) return;
      Preference.safeParse(body);
      await mirror.refetchClient(clientId).catch(() => undefined);
      return;
    }
    case 'duration': {
      // A1 — a remembered length lives inside its client's detail row, exactly
      // as a preference does, and the `{client_id, service_id, duration_min}`
      // the server answers with is not the whole truth about the client. Same
      // answer as above: refetch when we can, and let the next hydrate fix it
      // when we cannot. Until then the client screen reads the queued intent,
      // which is how the owner sees her own edit in a tunnel.
      const clientId = entry.target['client_id'];
      if (clientId === undefined) return;
      await mirror.refetchClient(clientId).catch(() => undefined);
      return;
    }
    case 'service': {
      const parsed = Service.safeParse(body);
      if (parsed.success) await mirror.putService(parsed.data);
      return;
    }
    /* S2 — a person or a room the owner just added or edited. */
    case 'resource': {
      const parsed = Resource.safeParse(body);
      if (parsed.success) await mirror.putResource(parsed.data);
      return;
    }
    case 'hours': {
      const parsed = HoursWritten.safeParse(body);
      if (parsed.success) {
        await mirror.putHours({ timezone, hours: parsed.data.hours });
      }
      return;
    }
    case 'blackout': {
      const parsed = Blackout.safeParse(body);
      if (parsed.success) await mirror.putBlackout(parsed.data);
      return;
    }
    /**
     * A checkout landed. Three things on this device are now out of date, and
     * none of them is the settlement itself — the document lives in the cloud
     * and the till has no reason to keep a copy.
     *
     * The **appointment** is the one that matters to the person looking at the
     * screen: `recordSettlement` completes the visit in the same commit, so the
     * row is refetched and Today redraws it as done. The certificate balance and
     * the guest's credit may both have moved, so their caches are refreshed too.
     *
     * All three are best-effort. This code runs the instant an intent settles,
     * which is the instant a connection exists — but a refetch that fails here
     * must not fail the settlement, because the settlement has already
     * committed. The next hydrate fixes whatever this missed.
     */
    case 'settlement': {
      const parsed = Settlement.safeParse(body);
      const appointmentId = parsed.success ? parsed.data.appointment_id : null;
      if (appointmentId !== null) {
        await mirror.refetchAppointment(appointmentId).catch(() => undefined);
      }
      if (parsed.success && parsed.data.applied !== undefined) {
        const applied = parsed.data.applied;
        if (applied.certificate_cents > 0) {
          await mirror.refreshCertificates().catch(() => undefined);
        }
        if (applied.gift_credit_cents > 0 || applied.cash_credit_cents > 0) {
          await mirror.refreshCreditBalances().catch(() => undefined);
        }
      }
      return;
    }
    case 'certificate': {
      const parsed = Certificate.safeParse(body);
      if (parsed.success) await mirror.putCertificate(parsed.data);
      return;
    }
    case 'credit': {
      // The response carries the new balances beside the entry it wrote, so the
      // counter sees the right number without a second round trip. The history
      // is refetched when it can be — a top-up made offline lands its balance
      // here and its history at the next hydrate.
      const parsed = CreditAdded.safeParse(body);
      if (!parsed.success) return;
      await mirror.putCreditBalances(parsed.data.client_id, {
        cash: parsed.data.balances.cash_cents,
        gift: parsed.data.balances.gift_cents,
      });
      await mirror.refetchCredit(parsed.data.client_id).catch(() => undefined);
      return;
    }
    case 'product': {
      const parsed = Product.safeParse(body);
      if (parsed.success) await mirror.putProduct(parsed.data);
      return;
    }
    case 'none':
      return;
  }
}

/**
 * The optimistic appointment: what the owner sees the instant they tap Book.
 *
 * Every field the calendar renders is present and every field the *server*
 * decides is a placeholder — `price_cents` is zero, and the id is a local one
 * that no server will ever mint. That asymmetry is the honest one: the device
 * may say "you asked for this", it may not say "this costs $120" (obligation
 * B7 — prices come from the catalog, and the catalog on this device may be a
 * week old).
 *
 * `source: 'staff'` or `'walk_in'` matches what the request asks for, so the row
 * does not visibly change category when it lands.
 */
export function optimisticAppointment(input: {
  serviceId: string;
  serviceName: string;
  resourceId: string;
  resourceName: string | null;
  /** The other lanes (0056), so the queued block is drawn in the room too. */
  alsoResourceIds?: readonly string[] | undefined;
  startIso: string;
  durationMin: number;
  clientId: string | null;
  clientName: string;
  source: 'staff' | 'walk_in';
  partySize?: number;
  /**
   * **What the catalog says this comes to** (migration 0048) — the option's
   * price plus every attached price, looked up on the device from its own
   * mirror of the menu.
   *
   * Absent keeps the `0` this row has always carried, which is what every
   * caller but the booking sheet passes. It is not a claim about what will be
   * charged: the server prices from the ids and this row is replaced the
   * moment the intent drains. It exists so the Today list and the till do not
   * read `$0` for a $300 facial in the minute before that happens.
   */
  priceCents?: number | undefined;
  /** The option this was booked at, and its name — `null` for a one-way service. */
  optionId?: string | null;
  optionName?: string | null;
  /** The attached lines, at their attached numbers. `[]` for nearly every row. */
  addOns?: readonly {
    service_id: string;
    name: string;
    price_cents: number;
    duration_min: number;
  }[];
  notes?: string | undefined;
  nowIso: string;
}): AppointmentType {
  const start = Date.parse(input.startIso);
  return {
    appointment_id: `local_${ulid()}`,
    status: 'confirmed',
    source: input.source,
    slot: {
      start: input.startIso,
      end: new Date(start + input.durationMin * 60_000).toISOString(),
      resource_id: input.resourceId,
      ...(input.alsoResourceIds === undefined || input.alsoResourceIds.length === 0
        ? {}
        : { also_resource_ids: [...input.alsoResourceIds] }),
    },
    resource_name: input.resourceName,
    service_id: input.serviceId,
    service_name: input.serviceName,
    duration_min: input.durationMin,
    party_size: input.partySize ?? 1,
    price_cents: input.priceCents ?? 0,
    discount_cents: 0,
    // Nothing has been given away yet, so the value *is* the price.
    value_cents: input.priceCents ?? 0,
    option_id: input.optionId ?? null,
    option_name: input.optionName ?? null,
    add_ons: [...(input.addOns ?? [])],
    notes: input.notes ?? null,
    provider_ref: null,
    client: {
      client_id: input.clientId ?? 'local_pending',
      display_name: input.clientName,
      phone: null,
    },
    created_at: input.nowIso,
    updated_at: input.nowIso,
    // A5: an appointment typed at the desk is never a request, so the four
    // request columns take their absent values. Written out rather than left
    // off, because the mirror stores this row and a screen that read
    // `request_state` off it would otherwise get `undefined` for a booking that
    // is perfectly confirmed.
    request_expires_at: null,
    request_proposed_by: 'guest',
    request_note: '',
    cancellation_reason: '',
  };
}

/** The local day an optimistic row belongs to, in the tenant's zone. */
export function optimisticDay(appointment: AppointmentType, session: Session | null): string {
  return isoDayIn(session?.tenant.timezone ?? 'UTC', appointment.slot.start);
}
